// 登录态：启动时用刷新 cookie 换访问令牌，再拉 /me；提供 login / register / logout，以及接收 Steam 登录签发的令牌。
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { onSessionChange, refreshSession, setAccessToken } from "@/lib/api/client";
import { ep, qk } from "@/lib/api/endpoints";
import { clearReplayCache } from "@/lib/assets/cache";
import type { Me, TokenResponse } from "@/lib/api/types";

type Status = "loading" | "authenticated" | "anonymous";

interface AuthCtx {
  status: Status;
  me: Me | undefined;
  meError: unknown;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  /** 接收其它方式（Steam）签发的令牌并进入登录态。 */
  accept: (t: TokenResponse) => void;
  logout: () => Promise<void>;
  refetchMe: () => Promise<unknown>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    let cancelled = false;
    refreshSession().then((t) => {
      if (!cancelled) setStatus(t ? "authenticated" : "anonymous");
    });
    const off = onSessionChange((t) => {
      if (!t) {
        setStatus("anonymous");
        qc.clear();
      }
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [qc]);

  const meQuery = useQuery({ queryKey: qk.me, queryFn: ep.me, enabled: status === "authenticated", staleTime: 60_000 });

  const accept = useCallback(
    (t: TokenResponse) => {
      setAccessToken(t.access_token);
      qc.clear();
      setStatus("authenticated");
    },
    [qc],
  );

  const login = useCallback(async (email: string, password: string) => accept(await ep.login(email, password)), [accept]);
  const register = useCallback(
    async (email: string, password: string, displayName: string) => accept(await ep.register(email, password, displayName)),
    [accept],
  );
  const logout = useCallback(async () => {
    try {
      await ep.logout();
    } finally {
      setAccessToken(null);
      qc.clear();
      void clearReplayCache();
      setStatus("anonymous");
    }
  }, [qc]);

  const value = useMemo<AuthCtx>(
    () => ({ status, me: meQuery.data, meError: meQuery.error, login, register, accept, logout, refetchMe: meQuery.refetch }),
    [status, meQuery.data, meQuery.error, meQuery.refetch, login, register, accept, logout],
  );
  return <Ctx value={value}>{children}</Ctx>;
}

export function useAuth() {
  const v = use(Ctx);
  if (!v) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return v;
}

/** 已登录页面里使用：保证 me 已加载。 */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error("useMe 只能在 RequireAuth 内使用");
  return me;
}
